import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { ProjectDomain, ReviewNote, ReviewRequest, ReviewStatus } from '../types';

type RawReviewStatus = ReviewStatus | 'submitted';

export interface AdminProject {
  id: string;
  title: string;
  ownerId: string;
  ownerName: string;
  createdAt: string | null;
  updatedAt: string | null;
  mainTexSize: number | null;
  reviewEnabled?: boolean;
  tags?: string[];
}

export interface AdminPerson {
  id: string;
  name: string;
  email: string;
  lastLoginAt: Date | null;
  projectCount: number;
}

export interface AdminPeoplePage {
  people: AdminPerson[];
  nextCursor: string | null;
}

export interface AdminReviewer {
  id: string;
  name: string;
  email: string;
  domains: ProjectDomain[];
}

export interface AdminDashboardResponse {
  stats: {
    totalProjects: number;
    totalUsers: number;
  };
  permissions: {
    canViewAllProjects: boolean;
    canViewReviewQueue: boolean;
    canViewApprovedProjects: boolean;
    canViewPeople: boolean;
    canViewReviewers: boolean;
  };
  projects: AdminProject[];
  reviewQueue: ReviewRequest[];
  approvedProjects: ReviewRequest[];
  completedProjects: ReviewRequest[];
  people: AdminPerson[];
  reviewers: AdminReviewer[];
  peopleNextCursor: string | null;
}

interface RawReviewRequest {
  id: string;
  projectId: string;
  ownerId: string;
  projectTitle: string;
  status: RawReviewStatus;
  submitterId: string;
  submitterName: string;
  submitterNotes: string;
  adminNotes?: string;
  tags?: string[];
  createdAt: string | null;
  updatedAt: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  hasPreviousApproval?: boolean;
  previousReviewCount?: number;
  noteEntries?: Array<{
    authorName: string;
    createdAt: string | null;
    source: ReviewNote['source'];
    status: RawReviewStatus;
    text: string;
  }>;
}

interface RawAdminDashboardResponse {
  stats: {
    totalProjects: number;
    totalUsers: number;
  };
  permissions: {
    canViewAllProjects: boolean;
    canViewReviewQueue: boolean;
    canViewApprovedProjects: boolean;
    canViewPeople: boolean;
    canViewReviewers: boolean;
  };
  projects: AdminProject[];
  reviewQueue: RawReviewRequest[];
  approvedProjects: RawReviewRequest[];
  completedProjects: RawReviewRequest[];
  reviewers: AdminReviewer[];
  peopleNextCursor?: string | null;
  people: Array<{
    id: string;
    name: string;
    email: string;
    lastLoginAt: string | null;
    projectCount: number;
  }>;
}

interface RawAdminPeoplePageResponse {
  people: Array<{
    id: string;
    name: string;
    email: string;
    lastLoginAt: string | null;
    projectCount: number;
  }>;
  nextCursor?: string | null;
}

function mapAdminPeoplePage(
  people: RawAdminPeoplePageResponse['people'],
  nextCursor?: string | null
): AdminPeoplePage {
  return {
    people: people.map((person) => ({
      ...person,
      lastLoginAt: person.lastLoginAt ? new Date(person.lastLoginAt) : null,
    })),
    nextCursor: nextCursor || null,
  };
}

function mapAdminReviewer(reviewer: AdminReviewer): AdminReviewer {
  return {
    ...reviewer,
    domains: reviewer.domains || [],
  };
}

function normalizeReviewStatus(status: RawReviewStatus | undefined): ReviewStatus {
  if (status === 'submitted' || !status) {
    return 'waiting_for_review';
  }

  return status;
}

function mapReviewRequest(review: RawReviewRequest): ReviewRequest {
  const createdAt = review.createdAt ? new Date(review.createdAt) : new Date();
  const updatedAt = review.updatedAt ? new Date(review.updatedAt) : new Date();
  const reviewedAt = review.reviewedAt ? new Date(review.reviewedAt) : null;

  return {
    ...review,
    status: normalizeReviewStatus(review.status),
    createdAt,
    updatedAt,
    reviewedAt,
    hasPreviousApproval: Boolean(review.hasPreviousApproval),
    previousReviewCount: typeof review.previousReviewCount === 'number'
      ? review.previousReviewCount
      : 0,
    noteEntries: (review.noteEntries || []).map((note) => ({
      ...note,
      status: normalizeReviewStatus(note.status),
      createdAt: note.createdAt ? new Date(note.createdAt) : updatedAt,
    })),
  };
}

export function subscribeToAdminReviewChanges(
  onChange: () => void,
  onError?: (error: Error) => void
): () => void {
  const reviewsQuery = query(
    collection(db, 'projectReviews'),
    orderBy('updatedAt', 'desc'),
    limit(200)
  );

  return onSnapshot(
    reviewsQuery,
    () => {
      onChange();
    },
    (error) => {
      console.error('Failed to subscribe to admin review changes:', error);
      onError?.(error);
    }
  );
}

export async function getAdminDashboard(): Promise<AdminDashboardResponse> {
  const fn = httpsCallable<void, RawAdminDashboardResponse>(functions, 'adminDashboard');
  const result = await fn();
  const peoplePage = mapAdminPeoplePage(
    result.data.people,
    result.data.peopleNextCursor
  );
  return {
    stats: result.data.stats,
    permissions: result.data.permissions,
    projects: result.data.projects,
    reviewQueue: result.data.reviewQueue.map(mapReviewRequest),
    approvedProjects: result.data.approvedProjects.map(mapReviewRequest),
    completedProjects: result.data.completedProjects.map(mapReviewRequest),
    people: peoplePage.people,
    reviewers: (result.data.reviewers || []).map(mapAdminReviewer),
    peopleNextCursor: peoplePage.nextCursor,
  };
}

export async function getAdminPeoplePage(cursor?: string | null): Promise<AdminPeoplePage> {
  const fn = httpsCallable<{ cursor?: string }, RawAdminPeoplePageResponse>(
    functions,
    'adminListPeople'
  );
  const result = await fn(cursor ? { cursor } : {});
  return mapAdminPeoplePage(result.data.people, result.data.nextCursor);
}

export async function updateReviewRequestStatus(input: {
  reviewId: string;
  status: ReviewStatus;
  notes?: string;
}): Promise<void> {
  const fn = httpsCallable<typeof input, void>(functions, 'adminUpdateReviewStatus');
  await fn(input);
}

export async function ensureAdminProjectCommentAccess(projectId: string): Promise<void> {
  const fn = httpsCallable<{ projectId: string }, void>(functions, 'adminGrantProjectCommentAccess');
  await fn({ projectId });
}

export async function adminSetProjectReviewEnabled(
  projectId: string,
  reviewEnabled: boolean
): Promise<void> {
  const fn = httpsCallable<{ projectId: string; reviewEnabled: boolean }, void>(
    functions,
    'adminSetProjectReviewEnabled'
  );
  await fn({ projectId, reviewEnabled });
}

export async function adminSetProjectTags(
  projectId: string,
  tags: string[]
): Promise<string[]> {
  const fn = httpsCallable<{ projectId: string; tags: string[] }, { tags: string[] }>(
    functions,
    'adminSetProjectTags'
  );
  const result = await fn({ projectId, tags });
  return result.data.tags;
}

export async function adminUpsertReviewer(input: {
  email: string;
  domains: ProjectDomain[];
}): Promise<AdminReviewer> {
  const fn = httpsCallable<typeof input, { reviewer: AdminReviewer }>(
    functions,
    'adminUpsertReviewer'
  );
  const result = await fn(input);
  return mapAdminReviewer(result.data.reviewer);
}
